import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Bell, Clock, AlertTriangle, CheckCircle, Settings, Play,
  Loader2, Filter, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const PRIORITY_COLORS = {
  LOW: 'bg-slate-100 text-slate-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  HIGH: 'bg-orange-100 text-orange-800',
  CRITICAL: 'bg-red-100 text-red-800'
};

const ALERT_TYPE_LABELS = {
  PRAZO_T10: 'Prazo T-10',
  PRAZO_T5: 'Prazo T-5',
  PRAZO_T1: 'Prazo T-1',
  VENCIDO: 'Vencido',
  EVIDENCIA_PENDENTE: 'Evidência Pendente',
  REVISAO_REJEITADA: 'Revisão Rejeitada',
  ATO_ESCALADO: 'Escalado',
  ATO_CRITICO: 'Crítico',
  ATO_JUDICIALIZADO: 'Judicializado',
  RISCO_ELEVADO: 'Risco Elevado',
  REINCIDENCIA: 'Reincidência'
};

export const AlertsPage = () => {
  const { api, hasPermission } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningCheck, setRunningCheck] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unresolved, critical

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [alertsRes, configRes] = await Promise.all([
        api.get('/alerts?limit=100'),
        api.get('/alerts/config')
      ]);
      setAlerts(alertsRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast.error('Erro ao carregar alertas');
    } finally {
      setLoading(false);
    }
  };

  const runManualCheck = async () => {
    setRunningCheck(true);
    try {
      const response = await api.post('/alerts/check');
      toast.success(`Verificação concluída: ${response.data.total_alerts} alertas gerados`);
      fetchData();
    } catch (error) {
      console.error('Error running check:', error);
      toast.error('Erro ao executar verificação');
    } finally {
      setRunningCheck(false);
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await api.put(`/alerts/${alertId}/resolve`);
      setAlerts(alerts.map(a => 
        a.alert_id === alertId ? { ...a, is_resolved: true } : a
      ));
      toast.success('Alerta resolvido');
    } catch (error) {
      toast.error('Erro ao resolver alerta');
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await api.put('/alerts/config', config);
      toast.success('Configurações salvas');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingConfig(false);
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'unresolved') return !alert.is_resolved;
    if (filter === 'critical') return alert.priority === 'CRITICAL';
    return true;
  });

  const unresolvedCount = alerts.filter(a => !a.is_resolved).length;
  const criticalCount = alerts.filter(a => a.priority === 'CRITICAL').length;
  const criticalUnresolvedCount = alerts.filter(a => a.priority === 'CRITICAL' && !a.is_resolved).length;

  if (loading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="alerts-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Sistema de Alertas</h1>
            <p className="text-slate-500 mt-1">
              Monitoramento automático de prazos e pendências
            </p>
          </div>
          <Button
            onClick={runManualCheck}
            disabled={runningCheck}
            className="bg-[#1d4ed8] hover:bg-[#1e40af]"
            data-testid="run-check-btn"
          >
            {runningCheck ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Executar Verificação
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1d4ed8]/10 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-[#1d4ed8]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{alerts.length}</p>
                  <p className="text-sm text-slate-500">Total de Alertas</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={unresolvedCount > 0 ? 'border-orange-200' : ''}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{unresolvedCount}</p>
                  <p className="text-sm text-slate-500">Não Resolvidos</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={criticalUnresolvedCount > 0 ? 'border-red-200' : ''}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{criticalUnresolvedCount}</p>
                  <p className="text-sm text-slate-500">Críticos Pendentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Alerts List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Alertas</CardTitle>
                  <div className="flex items-center gap-2">
                    <Select value={filter} onValueChange={setFilter}>
                      <SelectTrigger className="w-40" data-testid="filter-select">
                        <Filter className="w-4 h-4 mr-2" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="unresolved">Não Resolvidos</SelectItem>
                        <SelectItem value="critical">Críticos</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={fetchData}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {filteredAlerts.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <Bell className="w-12 h-12 mx-auto mb-4 opacity-20" />
                    <p>Nenhum alerta encontrado</p>
                  </div>
                ) : (
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Mensagem</TableHead>
                        <TableHead>Prioridade</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead className="w-20">Ação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAlerts.map((alert) => (
                        <TableRow
                          key={alert.alert_id}
                          className={alert.is_resolved ? 'opacity-50' : ''}
                          data-testid={`alert-row-${alert.alert_id}`}
                        >
                          <TableCell>
                            <Badge variant="outline">
                              {ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {alert.message}
                          </TableCell>
                          <TableCell>
                            <Badge className={PRIORITY_COLORS[alert.priority]}>
                              {alert.priority}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {format(new Date(alert.created_at), 'dd/MM HH:mm', { locale: ptBR })}
                          </TableCell>
                          <TableCell>
                            {!alert.is_resolved && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => resolveAlert(alert.alert_id)}
                                data-testid={`resolve-${alert.alert_id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Configuration Panel */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Configurações
                </CardTitle>
                <CardDescription>Configure o sistema de alertas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {config && (
                  <>
                    {/* Enable/Disable */}
                    <div className="flex items-center justify-between">
                      <Label>Alertas Ativos</Label>
                      <Switch
                        checked={config.enabled}
                        onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
                        data-testid="alerts-enabled-switch"
                      />
                    </div>

                    {/* Schedule */}
                    <div className="space-y-3">
                      <Label>Frequência de Verificação</Label>
                      <Select
                        value={config.schedule?.frequency || 'daily'}
                        onValueChange={(v) => setConfig({
                          ...config,
                          schedule: { ...config.schedule, frequency: v }
                        })}
                      >
                        <SelectTrigger data-testid="frequency-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">A cada hora</SelectItem>
                          <SelectItem value="daily">Diário</SelectItem>
                        </SelectContent>
                      </Select>

                      {config.schedule?.frequency === 'daily' && (
                        <div className="flex items-center gap-2">
                          <Label className="text-sm">Horário:</Label>
                          <Input
                            type="number"
                            min="0"
                            max="23"
                            className="w-16"
                            value={config.schedule?.hour || 8}
                            onChange={(e) => setConfig({
                              ...config,
                              schedule: { ...config.schedule, hour: parseInt(e.target.value) }
                            })}
                          />
                          <span>:</span>
                          <Input
                            type="number"
                            min="0"
                            max="59"
                            className="w-16"
                            value={config.schedule?.minute || 0}
                            onChange={(e) => setConfig({
                              ...config,
                              schedule: { ...config.schedule, minute: parseInt(e.target.value) }
                            })}
                          />
                        </div>
                      )}
                    </div>

                    {/* Thresholds */}
                    <div className="space-y-3">
                      <Label>Alertas de Prazo</Label>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">T-10 dias</span>
                          <Switch
                            checked={config.thresholds?.t10_enabled ?? true}
                            onCheckedChange={(v) => setConfig({
                              ...config,
                              thresholds: { ...config.thresholds, t10_enabled: v }
                            })}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">T-5 dias</span>
                          <Switch
                            checked={config.thresholds?.t5_enabled ?? true}
                            onCheckedChange={(v) => setConfig({
                              ...config,
                              thresholds: { ...config.thresholds, t5_enabled: v }
                            })}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">T-1 dia</span>
                          <Switch
                            checked={config.thresholds?.t1_enabled ?? true}
                            onCheckedChange={(v) => setConfig({
                              ...config,
                              thresholds: { ...config.thresholds, t1_enabled: v }
                            })}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Vencidos</span>
                          <Switch
                            checked={config.thresholds?.overdue_enabled ?? true}
                            onCheckedChange={(v) => setConfig({
                              ...config,
                              thresholds: { ...config.thresholds, overdue_enabled: v }
                            })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Evidence pending threshold */}
                    <div className="space-y-2">
                      <Label>Alerta de evidência pendente após (dias)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="30"
                        value={config.thresholds?.evidence_pending_days || 3}
                        onChange={(e) => setConfig({
                          ...config,
                          thresholds: { ...config.thresholds, evidence_pending_days: parseInt(e.target.value) }
                        })}
                      />
                    </div>

                    {/* Save button */}
                    {hasPermission('edit') && (
                      <Button
                        className="w-full bg-[#1d4ed8] hover:bg-[#1e40af]"
                        onClick={saveConfig}
                        disabled={savingConfig}
                        data-testid="save-config-btn"
                      >
                        {savingConfig ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : null}
                        Salvar Configurações
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default AlertsPage;
